import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Thread, ThreadWithMessages, ThreadNotification } from '@merkos-mgmt/shared';
import { UserRole } from '@merkos-mgmt/shared';

/** Someone the current user may address in a new conversation. */
export interface MessageContact {
  id: string;
  name: string;
  role: string;
}

/**
 * Recipients the caller may pick when composing. Staff get the whole active staff
 * directory; vendors only the staff on their projects plus the central contacts.
 */
export function useMessageContacts() {
  return useQuery({
    queryKey: ['mobile-message-contacts'],
    queryFn: () => api<MessageContact[]>('/threads/contacts'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Default recipient for an entity-linked conversation: the entity's creator, or —
 * when that's the caller themselves or someone they can't address — the first
 * accounting contact.
 */
export function getDefaultRecipientId(
  currentUserId: string | undefined,
  entityCreatedBy: string | undefined,
  contacts: MessageContact[],
): string | undefined {
  if (!entityCreatedBy) return undefined;
  if (currentUserId !== entityCreatedBy && contacts.some((c) => c.id === entityCreatedBy)) {
    return entityCreatedBy;
  }
  return contacts.find((c) => c.role === UserRole.Accounting)?.id;
}

export function useMyThreads() {
  return useQuery({
    queryKey: ['mobile-my-threads'],
    queryFn: () => api<ThreadNotification[]>('/threads/my'),
    staleTime: 20_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['mobile-unread-count'],
    queryFn: () => api<{ unread_count: number }>('/threads/unread-count'),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
}

export function useThread(id: string | undefined) {
  return useQuery({
    queryKey: ['mobile-thread', id],
    queryFn: () => api<ThreadWithMessages>(`/threads/${id}`),
    enabled: !!id,
  });
}

export function useEntityThreads(entityType: string | null, entityId: string | null) {
  return useQuery({
    queryKey: ['mobile-entity-threads', entityType, entityId],
    queryFn: () => api<Thread[]>(`/threads?entity_type=${entityType}&entity_id=${entityId}`),
    enabled: !!(entityType && entityId),
  });
}

export interface MobileCreateThreadInput {
  subject?: string;
  body: string;
  entity_type?: string;
  entity_id?: string;
  recipient_ids: string[];
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MobileCreateThreadInput) =>
      api<Thread>('/threads', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mobile-my-threads'] }); },
  });
}

export function useAddMessage(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { body: string }) =>
      api(`/threads/${threadId}/messages`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mobile-thread', threadId] });
      qc.invalidateQueries({ queryKey: ['mobile-my-threads'] });
    },
  });
}

export function useMarkRead(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/threads/${threadId}/read`, { method: 'PUT', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mobile-unread-count'] });
      qc.invalidateQueries({ queryKey: ['mobile-my-threads'] });
    },
  });
}

export function useUpdateThreadStatus(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { status: string }) =>
      api(`/threads/${threadId}/status`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mobile-thread', threadId] });
      qc.invalidateQueries({ queryKey: ['mobile-my-threads'] });
    },
  });
}

export type ThreadEntityType = 'expense' | 'time_entry' | 'budget' | 'employee' | 'vendor' | 'department' | 'project';

export function getEntityLabel(entityType: ThreadEntityType | null | undefined): string {
  if (!entityType) return 'General';
  const labels: Record<string, string> = {
    expense: 'Expense',
    time_entry: 'Time Entry',
    budget: 'Budget',
    employee: 'Employee',
    vendor: 'Vendor',
    department: 'Department',
    project: 'Project',
  };
  return labels[entityType] || entityType;
}

export function getEntityUrl(entityType: ThreadEntityType | null | undefined, entityId: string | null | undefined): string | null {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case 'expense':    return `/expenses?preview=${entityId}`;
    case 'time_entry': return `/time?highlight=${entityId}`;
    default:           return null;
  }
}
